import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Stack, Button, IconButton, Box,
  Typography, Chip, Autocomplete, Divider, Alert,
  Accordion, AccordionSummary, AccordionDetails,
  Select, FormControl, InputLabel, Grid, Tooltip,
} from '@mui/material';
import {
  PushPin, PushPinOutlined, Close, ExpandMore,
  Add, Delete, Save, SaveAlt,
} from '@mui/icons-material';
import {
  BAUMARTEN, FUNKTIONEN, VITALITAETEN, ZUSTAENDE, ENTWICKLUNGEN, KONTROLL_INTERVALLE,
  SCHADEN_BEREICHE, SCHADEN_BESCHREIBUNGEN, PRIORITAETEN, SCHWEREGRADE,
} from '../data/feldOptionen';
import { GPSPosition } from '../services/gps';

export interface BaumFormData {
  baumnummer: string;
  baumart: string;
  ort: string;
  strasse: string;
  funktion: string;
  hoehe: string;
  stammdurchmesser: string;
  stammumfang: string;
  kronendurchmesser: string;
  kronenansatz: string;
  vitalitaet: string;
  zustand: string;
  entwicklung: string;
  kontroll_intervall: string;
  pflanzjahr: string;
  bemerkung: string;
  gefaellt: boolean;
}

export interface SchadenData {
  bereich: string;
  beschreibung: string;
  prioritaet: string;
  schweregrad: string;
  bemerkung: string;
}

export const emptyForm = (): BaumFormData => ({
  baumnummer: '', baumart: '', ort: '', strasse: '', funktion: '',
  hoehe: '', stammdurchmesser: '', stammumfang: '',
  kronendurchmesser: '', kronenansatz: '', vitalitaet: '', zustand: '',
  entwicklung: '', kontroll_intervall: '', pflanzjahr: '', bemerkung: '',
  gefaellt: false,
});

interface Props {
  open: boolean;
  onClose: () => void;
  position: GPSPosition | null;
  initial: BaumFormData;
  initialSchaden: SchadenData[];
  pinned: Set<keyof BaumFormData>;
  onPinChange: (field: keyof BaumFormData, pinned: boolean) => void;
  onSave: (
    data: BaumFormData,
    schaden: SchadenData[],
    mode: 'uebernehmen' | 'anpassen-erledigt'
  ) => Promise<void>;
  presets: { id: string; name: string; daten: any }[];
  onPresetSelect: (presetId: string) => void;
  onPresetSave: (name: string) => void;
}

export default function ErfassungsDialog({
  open, onClose, position, initial, initialSchaden,
  pinned, onPinChange, onSave, presets, onPresetSelect, onPresetSave,
}: Props) {
  const [form, setForm] = useState<BaumFormData>(initial);
  const [schaden, setSchaden] = useState<SchadenData[]>(initialSchaden);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [presetName, setPresetName] = useState('');
  const [showPresetSave, setShowPresetSave] = useState(false);

  useEffect(() => { setForm(initial); }, [initial, open]);
  useEffect(() => { setSchaden(initialSchaden); }, [initialSchaden, open]);

  const update = (field: keyof BaumFormData, value: any) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleSave = async (mode: 'uebernehmen' | 'anpassen-erledigt') => {
    if (!form.baumart) { setError('Baumart erforderlich'); return; }
    if (!position) { setError('Keine GPS-Position'); return; }
    setBusy(true); setError('');
    try {
      await onSave(form, schaden, mode);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Speichern fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  // Pin-Toggle-Komponente für ein Feld
  const PinButton = ({ field }: { field: keyof BaumFormData }) => (
    <Tooltip title={pinned.has(field) ? 'Wert gemerkt – wird beim nächsten Baum übernommen' : 'Wert merken'}>
      <IconButton
        size="small"
        onClick={() => onPinChange(field, !pinned.has(field))}
        color={pinned.has(field) ? 'primary' : 'default'}
        sx={{ ml: -0.5 }}
      >
        {pinned.has(field) ? <PushPin fontSize="small" /> : <PushPinOutlined fontSize="small" />}
      </IconButton>
    </Tooltip>
  );

  const addSchaden = () => {
    setSchaden((s) => [...s, {
      bereich: 'Krone', beschreibung: '', prioritaet: 'mittel',
      schweregrad: 'Mittel', bemerkung: '',
    }]);
  };

  const updateSchaden = (i: number, field: keyof SchadenData, value: any) => {
    setSchaden((s) => s.map((sd, idx) => idx === i ? { ...sd, [field]: value } : sd));
  };

  const removeSchaden = (i: number) => {
    setSchaden((s) => s.filter((_, idx) => idx !== i));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        🌳 Baum erfassen
        <IconButton onClick={onClose} size="small"><Close /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* GPS-Position-Anzeige */}
        {position ? (
          <Alert severity="success" icon={false} sx={{ mb: 2 }}>
            <Typography variant="body2">
              📍 <strong>{position.latitude.toFixed(5)}°N, {position.longitude.toFixed(5)}°O</strong>
              <br />
              {position.quality === 'handy'
                ? `Handy-GPS · ±${(position.accuracy_mm / 1000).toFixed(1)} m`
                : `${position.quality.toUpperCase()} · ±${position.accuracy_mm} mm`}
            </Typography>
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>Keine GPS-Position verfügbar</Alert>
        )}

        {/* Presets */}
        {presets.length > 0 && (
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Preset</InputLabel>
            <Select
              label="Preset"
              defaultValue=""
              onChange={(e) => e.target.value && onPresetSelect(String(e.target.value))}
            >
              <MenuItem value="">— kein Preset —</MenuItem>
              {presets.map((p) => (
                <MenuItem key={p.id} value={p.id}>📋 {p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Stack spacing={2}>
          {/* Baumnummer */}
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <TextField
              label="Baumnummer"
              value={form.baumnummer}
              onChange={(e) => update('baumnummer', e.target.value)}
              fullWidth
              size="small"
              helperText="Auto-Vorschlag oder Plaketten-Nummer eintragen"
            />
            <PinButton field="baumnummer" />
          </Stack>

          {/* Baumart */}
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Autocomplete
              freeSolo
              options={BAUMARTEN}
              value={form.baumart}
              onChange={(_, v) => update('baumart', v || '')}
              onInputChange={(_, v) => update('baumart', v || '')}
              sx={{ flexGrow: 1 }}
              renderInput={(params) => (
                <TextField {...params} label="Baumart *" size="small" required />
              )}
            />
            <PinButton field="baumart" />
          </Stack>

          {/* Ort + Straße */}
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <TextField label="Ort" value={form.ort} onChange={(e) => update('ort', e.target.value)}
              fullWidth size="small" />
            <PinButton field="ort" />
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <TextField label="Straße" value={form.strasse} onChange={(e) => update('strasse', e.target.value)}
              fullWidth size="small" />
            <PinButton field="strasse" />
          </Stack>

          {/* Funktion */}
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Autocomplete
              freeSolo
              options={FUNKTIONEN}
              value={form.funktion}
              onChange={(_, v) => update('funktion', v || '')}
              onInputChange={(_, v) => update('funktion', v || '')}
              sx={{ flexGrow: 1 }}
              renderInput={(params) => <TextField {...params} label="Funktion" size="small" />}
            />
            <PinButton field="funktion" />
          </Stack>

          {/* Maße */}
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <TextField label="Höhe (m)" type="number" value={form.hoehe}
                  onChange={(e) => update('hoehe', e.target.value)} fullWidth size="small" />
                <PinButton field="hoehe" />
              </Stack>
            </Grid>
            <Grid item xs={6}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <TextField label="Stammdm (cm)" type="number" value={form.stammdurchmesser}
                  onChange={(e) => update('stammdurchmesser', e.target.value)} fullWidth size="small" />
                <PinButton field="stammdurchmesser" />
              </Stack>
            </Grid>
            <Grid item xs={6}>
              <TextField label="Stammumf. (cm)" type="number" value={form.stammumfang}
                onChange={(e) => update('stammumfang', e.target.value)} fullWidth size="small" />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Kronendm (m)" type="number" value={form.kronendurchmesser}
                onChange={(e) => update('kronendurchmesser', e.target.value)} fullWidth size="small" />
            </Grid>
          </Grid>

          {/* Weiterführende Felder */}
          <Accordion elevation={0} sx={{ '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider' }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="body2" fontWeight={600}>+ Weitere Felder</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Vitalität</InputLabel>
                  <Select label="Vitalität" value={form.vitalitaet}
                    onChange={(e) => update('vitalitaet', e.target.value)}>
                    <MenuItem value="">— keine Angabe —</MenuItem>
                    {VITALITAETEN.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Zustand</InputLabel>
                  <Select label="Zustand" value={form.zustand}
                    onChange={(e) => update('zustand', e.target.value)}>
                    <MenuItem value="">— keine Angabe —</MenuItem>
                    {ZUSTAENDE.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Entwicklungsphase</InputLabel>
                  <Select label="Entwicklungsphase" value={form.entwicklung}
                    onChange={(e) => update('entwicklung', e.target.value)}>
                    <MenuItem value="">— keine Angabe —</MenuItem>
                    {ENTWICKLUNGEN.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Kontroll-Intervall</InputLabel>
                  <Select label="Kontroll-Intervall" value={form.kontroll_intervall}
                    onChange={(e) => update('kontroll_intervall', e.target.value)}>
                    <MenuItem value="">— keine Angabe —</MenuItem>
                    {KONTROLL_INTERVALLE.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField label="Pflanzjahr" type="number" value={form.pflanzjahr}
                  onChange={(e) => update('pflanzjahr', e.target.value)} fullWidth size="small" />
                <TextField label="Bemerkung" value={form.bemerkung}
                  onChange={(e) => update('bemerkung', e.target.value)}
                  fullWidth size="small" multiline rows={2} />
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Schäden */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                Schäden ({schaden.length})
              </Typography>
              <Button size="small" startIcon={<Add />} onClick={addSchaden}>
                Hinzufügen
              </Button>
            </Stack>
            <Stack spacing={1.5}>
              {schaden.map((s, i) => (
                <Box key={i} sx={{
                  border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5,
                }}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Chip size="small" label={`Schaden ${i + 1}`} />
                    <IconButton size="small" onClick={() => removeSchaden(i)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Stack>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Bereich</InputLabel>
                        <Select label="Bereich" value={s.bereich}
                          onChange={(e) => updateSchaden(i, 'bereich', e.target.value)}>
                          {SCHADEN_BEREICHE.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Schweregrad</InputLabel>
                        <Select label="Schweregrad" value={s.schweregrad}
                          onChange={(e) => updateSchaden(i, 'schweregrad', e.target.value)}>
                          {SCHWEREGRADE.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Autocomplete
                        freeSolo
                        options={SCHADEN_BESCHREIBUNGEN[s.bereich] || []}
                        value={s.beschreibung}
                        onChange={(_, v) => updateSchaden(i, 'beschreibung', v || '')}
                        onInputChange={(_, v) => updateSchaden(i, 'beschreibung', v || '')}
                        renderInput={(params) => (
                          <TextField {...params} label="Beschreibung" size="small" />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Priorität</InputLabel>
                        <Select label="Priorität" value={s.prioritaet}
                          onChange={(e) => updateSchaden(i, 'prioritaet', e.target.value)}>
                          {PRIORITAETEN.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Stack>
          </Box>

          {/* Preset speichern */}
          {!showPresetSave ? (
            <Button size="small" startIcon={<SaveAlt />} onClick={() => setShowPresetSave(true)}>
              Aktuelle Werte als Preset speichern
            </Button>
          ) : (
            <Stack direction="row" spacing={1}>
              <TextField size="small" label="Preset-Name" fullWidth value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                placeholder="z.B. Allee Linden" />
              <Button variant="contained" size="small" disabled={!presetName.trim()}
                onClick={() => { onPresetSave(presetName); setPresetName(''); setShowPresetSave(false); }}>
                Speichern
              </Button>
              <Button size="small" onClick={() => setShowPresetSave(false)}>Abbrechen</Button>
            </Stack>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={busy}>Abbrechen</Button>
        <Button
          variant="outlined"
          startIcon={<Save />}
          onClick={() => handleSave('anpassen-erledigt')}
          disabled={busy || !position || !form.baumart}
        >
          Speichern + schließen
        </Button>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={() => handleSave('uebernehmen')}
          disabled={busy || !position || !form.baumart}
        >
          Übernehmen + nächster
        </Button>
      </DialogActions>
    </Dialog>
  );
}
